import { lazy, Suspense, useState, useEffect, Component, type ReactNode } from 'react'
import { DynamicOrb } from './DynamicOrb'
import type { OrbState } from './DynamicOrb'

const WebGLOrbLazy = lazy(() => import('./WebGLOrb').then(m => ({ default: m.WebGLOrb })))

// Error boundary that silently falls back to CSS orb
class OrbErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch(error: Error) {
    console.warn('WebGL orb failed, using CSS fallback:', error.message)
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children
  }
}

// Check WebGL support without importing Three.js
let _hasWebGL: boolean | null = null
function checkWebGL(): boolean {
  if (_hasWebGL !== null) return _hasWebGL
  try {
    const canvas = document.createElement('canvas')
    _hasWebGL = !!(canvas.getContext('webgl2') || canvas.getContext('webgl'))
  } catch {
    _hasWebGL = false
  }
  return _hasWebGL
}

interface LazyOrbProps {
  size?: 'full' | 'compact'
  state?: OrbState
  volume?: number
  onTap?: () => void
  onLongPress?: () => void
}

export function LazyWebGLOrb({ size = 'full', state = 'idle', volume = 0, onTap, onLongPress }: LazyOrbProps) {
  const [useWebGL, setUseWebGL] = useState(false)

  useEffect(() => {
    if (size === 'full' && checkWebGL()) {
      setUseWebGL(true)
    }
  }, [size])

  const cssOrb = <DynamicOrb size={size} state={state} volume={volume} onTap={onTap} onLongPress={onLongPress} />

  if (!useWebGL || size === 'compact') {
    return cssOrb
  }

  return (
    <OrbErrorBoundary fallback={cssOrb}>
      <Suspense fallback={cssOrb}>
        <WebGLOrbLazy state={state} volume={volume} onTap={onTap} onLongPress={onLongPress} />
      </Suspense>
    </OrbErrorBoundary>
  )
}
